import { useGetBreeds } from '@application/api';
import {
  BE_SUPPORTED_REGIONS,
  DE_SUPPORTED_REGIONS,
  FR_SUPPORTED_REGIONS,
  NL_SUPPORTED_REGIONS,
  CH_SUPPORTED_REGIONS,
  SUPPORTED_COUNTRIES,
} from '@application/constants';
import { LANGUAGE_CODES } from '@application/constants/language';
import { useRouter, useValidation } from '@application/utils';
import { CountryCode } from '@gql/graphql';
import { useMemo } from 'react';
import { array, mixed, number, object, string } from 'yup';

export const DOCUMENT_FILE_MAX_SIZE = 8 * 1000000;

export const useValidationSchema = () => {
  const { language } = useRouter();
  const {
    invalidMessage,
    requiredMessage,
    fileTooLargeMessage,
    siretMessageFR,
    agreementMessageNL,
  } = useValidation();
  const { breedOptions } = useGetBreeds();
  const onboardingFormValidationSchemas = useMemo(() => {
    return [
      //step 0
      object({}),
      // Step 1 validation
      object({
        spokenLanguages: array()
          .of(string().oneOf(LANGUAGE_CODES, invalidMessage))
          .min(1, requiredMessage)
          .required(requiredMessage),
        city: string().required(requiredMessage),
        zip: string().when('country', ([country], schema) => {
          switch (country) {
            case CountryCode.BE:
              return schema.length(4, invalidMessage).required(requiredMessage);
            case CountryCode.CH:
              return schema.length(4, invalidMessage).required(requiredMessage);
            case CountryCode.NL:
              return schema
                .test('zip', invalidMessage, (value) => {
                  if (!value) return false;
                  return new RegExp('^[0-9]{4} ?([a-z]{2})?$', 'i').test(value);
                })
                .required(requiredMessage);
            default: //FR + DE
              return schema
                .test('zip', invalidMessage, (value) => {
                  if (!value) return false;
                  return new RegExp(/\d{5}/g).test(value);
                })
                .required(requiredMessage);
          }
        }),
        region: string()
          .oneOf([
            ...FR_SUPPORTED_REGIONS,
            ...BE_SUPPORTED_REGIONS,
            ...NL_SUPPORTED_REGIONS,
            ...DE_SUPPORTED_REGIONS,
            ...CH_SUPPORTED_REGIONS,
          ])
          .required(requiredMessage),
        country: string().oneOf(SUPPORTED_COUNTRIES).required(requiredMessage),
      }),
      // Step 2 validation
      object({
        level: string().when('country', {
          is: CountryCode.CH,
          then: (schema) => schema.optional().nullable(),
          otherwise: (schema) =>
            schema
              .required(requiredMessage)
              .oneOf(['1', '2', '3', '4', '5', '6', '7', '8'], invalidMessage),
        }),
        agreementNumber: string().when('country', ([country], schema) => {
          switch (country) {
            case CountryCode.FR:
              return schema.when('level', {
                is: '4',
                then: () => schema.optional().nullable(),
                otherwise: () =>
                  schema
                    .test('agreementNumber', siretMessageFR, (value) => {
                      if (!value) return true;
                      return (
                        new RegExp('^\\d{9}$').test(value) || new RegExp('^\\d{14}$').test(value)
                      );
                    })
                    .required(requiredMessage),
              });
            case CountryCode.NL:
              return schema
                .test('agreementNumber', agreementMessageNL, (value) => {
                  if (!value) return false;
                  return new RegExp('^[0-9]{2,7}$').test(value);
                })
                .required(requiredMessage);
            case CountryCode.BE:
              return schema.required(requiredMessage);
            default: // DE + CH
              return schema.optional().nullable();
          }
        }),
      }),
      // Step 3 validation
      object({
        breedingName: string().when('level', {
          is: '4',
          then: (schema) => schema.required(requiredMessage),
          otherwise: (schema) =>
            schema.when('country', {
              is: CountryCode.CH,
              then: (subSchema) => subSchema.required(requiredMessage),
              otherwise: (subSchema) => subSchema.optional(),
            }),
        }),
        dogBreedsIds: array()
          .of(
            number().oneOf(
              breedOptions.map((option) => option.value),
              invalidMessage,
            ),
          )
          .min(1, requiredMessage)
          .required(requiredMessage),
        dogsPedigree: string().nullable().not([undefined], requiredMessage),
      }),
      // Step 4 Validation
      object({
        websiteUrl: string().optional().nullable(),
        facebookUrl: string().optional().nullable(),
      }),
      // Step 5 Validation
      object({
        translated: object({
          [language]: object({
            description: string().required(requiredMessage),
          }),
        }),
      }),
      // Step 6 validation
      object({
        femaleBreedersCount: number().required(requiredMessage).min(1, requiredMessage),
        motherRestBetweenLitters: string().required(requiredMessage),
        littersByFemale: string().required(requiredMessage),
      }),
      // Step 7 validation
      object({
        medicalTestsPerformed: string().required(requiredMessage),
        dogsConsanguinity: string().required(requiredMessage),
      }),
      // Step 8 validation
      object({
        veterinaryInfo: string().required(requiredMessage),
      }),
      // Step 9 validation
      object({
        translated: object({
          [language]: object({
            dogsLivingPlace: string().required(requiredMessage),
            puppiesLivingPlace: string().required(requiredMessage),
          }),
        }),
      }),
      // Step 10 validation
      object({
        translated: object({
          [language]: object({
            breederDogsSelection: string().required(requiredMessage),
            puppiesTraining: string().required(requiredMessage),
          }),
        }),
      }),
      // Step 11 validation
      object({
        profileImage: mixed().required(requiredMessage),
        images: array()
          .of(mixed())
          .test('is-valid', requiredMessage, (value) => {
            return Array.isArray(value) && value.filter((item) => !!item).length > 0;
          }),
      }),

      // Step 12 validation
      object({
        hkFile: mixed()
          .when('country', {
            is: CountryCode.NL,
            then: (schema) => schema.required(requiredMessage),
            otherwise: (schema) => schema.optional(),
          })
          .test('fileSize', fileTooLargeMessage, (value) => {
            return value?.size ? value.size <= DOCUMENT_FILE_MAX_SIZE : true;
          }),
        permissionFiles: array()
          .of(mixed())
          .optional()
          .nullable()
          .test('fileSize', fileTooLargeMessage, (value) => {
            return value?.size ? value.size <= DOCUMENT_FILE_MAX_SIZE : true;
          }),
        identityFiles: array()
          .of(mixed())
          .optional()
          .nullable()
          .test('fileSize', fileTooLargeMessage, (value) => {
            return value?.size ? value.size <= DOCUMENT_FILE_MAX_SIZE : true;
          }),
        breedClubFiles: array()
          .of(mixed())
          .optional()
          .nullable()
          .test('fileSize', fileTooLargeMessage, (value) => {
            return value?.size ? value.size <= DOCUMENT_FILE_MAX_SIZE : true;
          }),
        certificationFile: mixed()
          .test('fileSize', fileTooLargeMessage, (value) => {
            return value?.size ? value.size <= DOCUMENT_FILE_MAX_SIZE : true;
          })
          .optional(),
      }),
    ];
  }, [
    agreementMessageNL,
    breedOptions,
    fileTooLargeMessage,
    invalidMessage,
    language,
    requiredMessage,
    siretMessageFR,
  ]);

  return { onboardingFormValidationSchemas };
};
